import type { Consent, Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SettingsTaskContext } from './SettingsTaskContext.base';
import { countMissingRequiredConsents, mergeIntakeInformedConsent } from './settings.utils';

export function SettingsTaskProvider({ children }: { children: ReactNode }): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [taskCount, setTaskCountState] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const setTaskCount = useCallback((count: number) => setTaskCountState(count), []);

  // Fetch count on mount/remount so the sidebar badge is always accurate
  useEffect(() => {
    const patientId = profile?.id;
    if (!patientId) {
      queueMicrotask(() => setHasLoaded(true));
      return;
    }
    Promise.all([
      medplum.searchResources('Consent', { patient: `Patient/${patientId}` }),
      medplum.searchResources('QuestionnaireResponse', {
        subject: `Patient/${patientId}`,
        _sort: '-authored',
        _count: '50',
      }),
    ])
      .then(([consents, questionnaireResponses]: [Consent[], QuestionnaireResponse[]]) => {
        setTaskCountState(
          countMissingRequiredConsents(mergeIntakeInformedConsent(consents, questionnaireResponses, profile))
        );
        queueMicrotask(() => setHasLoaded(true));
      })
      .catch(() => {
        // Silently fail — badge just won't show
        queueMicrotask(() => setHasLoaded(true));
      });
  }, [medplum, profile]);

  const value = useMemo(() => ({ taskCount, setTaskCount, hasLoaded }), [taskCount, setTaskCount, hasLoaded]);

  return <SettingsTaskContext.Provider value={value}>{children}</SettingsTaskContext.Provider>;
}
